import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export interface HeaderRow {
  name: string;
  /** Plaintext the user is entering now. Empty + secret + hasValue means "keep". */
  value: string;
  secret: boolean;
  /** Whether the server already has a stored value for this (secret) header. */
  hasValue: boolean;
}

export function emptyHeaderRow(): HeaderRow {
  return { name: '', value: '', secret: false, hasValue: false };
}

interface HeaderEditorProps {
  rows: HeaderRow[];
  onChange: (rows: HeaderRow[]) => void;
}

/** Dynamic editor for a connector's headers, with a write-only secret toggle. */
export function HeaderEditor({ rows, onChange }: HeaderEditorProps) {
  function updateRow(index: number, patch: Partial<HeaderRow>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Headers</label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...rows, emptyHeaderRow()])}
          data-testid="add-header-button"
        >
          Add header
        </Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-gray-500">No headers.</p>}
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 p-3 sm:grid-cols-2"
          data-testid="header-row"
        >
          <Input
            label="Name"
            value={row.name}
            onChange={(e) => updateRow(index, { name: e.target.value })}
            placeholder="X-Api-Key"
            data-testid="header-name-input"
          />
          <Input
            label="Value"
            type={row.secret ? 'password' : 'text'}
            value={row.value}
            onChange={(e) => updateRow(index, { value: e.target.value })}
            placeholder={row.secret && row.hasValue ? 'Value set - leave blank to keep it' : '<token>'}
            data-testid="header-value-input"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={row.secret}
              onChange={(e) => updateRow(index, { secret: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              data-testid="header-secret-checkbox"
            />
            Secret (encrypted, write-only)
          </label>
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeRow(index)}
              data-testid="remove-header-button"
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
